import PageCard from 'src/components/PageCard';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useParams } from 'react-router-dom';
import {
  CustomerAddress,
  MigrationStatus,
  PortalRole,
  useGetDeviceDetailsQuery,
  useLazyGetDeviceMigrationStatusQuery,
  useResetDevicesStatusMutation,
} from 'src/backend';
import DataField from 'src/components/DataField/DataField';
import { Divider, Stack, Typography } from '@mui/material';
import OnOff from 'src/components/OnOff';
import { useCallback, useEffect, useState } from 'react';
import LoadingButton from 'src/components/LoadingButton';
import RoleBased from 'src/components/RoleBased';
import Loader from 'src/components/Loader';

type Params = {
  id: string;
};

function formatAddress(address?: CustomerAddress) {
  if (!address) {
    return '-';
  }
  const hn = address.houseNumber ? `${address.houseNumber} ` : '';
  const st = address.street ? `${address.street}` : '';
  const street = (hn + st).trim();
  const streetLine = street ? (
    <Typography variant="caption" align="right">
      {street}
    </Typography>
  ) : (
    ''
  );
  const regCode = address.regionCode ? `${address.regionCode}` : '';
  const postCode = address.postCode ? `${address.postCode}` : '';
  const cty = address.city ? `${address.city}` : '';

  const city = `${regCode + postCode} ${cty}`.trim();
  const cityLine = city ? (
    <Typography variant="caption" align="right">
      {city}
    </Typography>
  ) : (
    ''
  );
  const countryLine = address.country ? (
    <Typography variant="caption" align="right">
      {address.country}
    </Typography>
  ) : (
    ''
  );

  return (
    <Stack>
      {streetLine}
      {cityLine}
      {countryLine}
    </Stack>
  );
}

export default function DeviceDetailsOverview() {
  const { id } = useParams<Params>();
  const { data, isLoading } = useGetDeviceDetailsQuery(id ?? '');
  const [getDeviceMigrationStatus] = useLazyGetDeviceMigrationStatusQuery();
  const resetDevicesStatus = useResetDevicesStatusMutation();
  const overview = data?.overview;
  const [deviceGroup, setDeviceGroup] = useState('-');
  const [brokerVersion, setBrokerVersion] = useState('-');
  const [deviceStatus, setDeviceStatus] = useState('-');
  const [isResettingState, setIsResettingState] = useState(false);
  const [isFetchingMigrationStatus, setIsFetchinMigrationStatus] =
    useState(false);

  const setMigrationStatusInfo = useCallback(
    (status: MigrationStatus) => {
      if (status && status.groupName) {
        setDeviceGroup(status.groupName);
      } else {
        setDeviceGroup('-');
      }
      if (status && status.brokerVersion) {
        setBrokerVersion(status.brokerVersion);
      } else {
        setBrokerVersion('-');
      }
      if (status && status.deviceStatus) {
        setDeviceStatus(status.deviceStatus);
      } else {
        setDeviceStatus('-');
      }
    },
    [setDeviceGroup, setBrokerVersion, setDeviceStatus],
  );

  const getMigrationStatus = useCallback(() => {
    if (data && data.overview) {
      setIsFetchinMigrationStatus(true);
      getDeviceMigrationStatus(data.overview.serialNumber)
        .unwrap()
        .then((result) => {
          setMigrationStatusInfo(result.data);
        })
        .finally(() => setIsFetchinMigrationStatus(false));
    }
  }, [data, getDeviceMigrationStatus, setMigrationStatusInfo]);

  useEffect(() => {
    if (isLoading === false) {
      getMigrationStatus();
    }
  }, [getMigrationStatus, isLoading]);

  const resetStatus = useCallback(() => {
    if (data && data.overview) {
      setIsResettingState(true);
      resetDevicesStatus[0]([data.overview.serialNumber])
        .unwrap()
        .then(() => {
          setIsResettingState(false);
          getMigrationStatus();
        });
    }
  }, [data, setIsResettingState, resetDevicesStatus, getMigrationStatus]);

  return isLoading ? (
    ''
  ) : (
    <PageCard title="Overview" icon={<InfoOutlinedIcon />}>
      <DataField label="Twin ID" value={data?.overview.twinId} />
      <DataField label="Serial number" value={data?.overview.serialNumber} />
      <DataField
        label="Connected"
        value={
          <OnOff
            value={overview?.isConnected}
            onText="Online"
            offText="Offline"
            width="10rem"
          />
        }
      />
      <Divider sx={{ paddingTop: '1em' }} />
      <DataField label="Customer name" value={overview?.customerName} />
      <DataField label="Customer ID" value={overview?.customerId} />
      <DataField label="Technician" value={overview?.assignedTechnician} />
      <Divider sx={{ paddingTop: '1em' }} />
      <DataField
        label="Address"
        value={formatAddress(overview?.customerAddress)}
      />
      {isFetchingMigrationStatus ? (
        <>
          <Divider sx={{ paddingTop: '2em', marginBottom: '2em' }} />
          <Loader />
        </>
      ) : (
        <RoleBased
          shouldRedirect={false}
          requiredRole={PortalRole[PortalRole.Migrator]}
          component={
            <>
              <Divider sx={{ paddingTop: '2em' }} />
              {deviceStatus !== 'finished' && (
                <>
                  <DataField label="Migration group:" value={deviceGroup} />
                  <DataField label="Migration status:" value={deviceStatus} />
                </>
              )}
              <DataField label="Broker version:" value={brokerVersion} />
              <DataField
                label=""
                value={
                  <LoadingButton
                    loading={isResettingState}
                    onClick={resetStatus}
                    variant="outlined"
                    size="small"
                  >
                    Reset migration status
                  </LoadingButton>
                }
              />
            </>
          }
        />
      )}
    </PageCard>
  );
}
